class SupplementQuiz {
  constructor(root) {
    this.root = root;
    this.locale = (root.dataset.locale || document.documentElement.lang || 'zh-TW').toLowerCase();
    this.messages = this.getMessages();
    this.screens = [...root.querySelectorAll('[data-quiz-screen]')];
    this.categories = new Map();
    this.resultProducts = [];
    this.currentScreen = 'intro';
    this.analyticsVersion = root.dataset.analyticsVersion || '2026-09';
    this.analyticsEndpoint = root.dataset.analyticsEndpoint || '';
    this.readCategories();
    this.bindEvents();
    this.showScreen('intro');
  }

  getMessages() {
    const zh = {
      start: '開始', maxGoals: '最多可以選擇 3 個方向。', chooseGoal: '請至少選擇 1 個最關注方向。', chooseAge: '請選擇適用年齡組別。', confirmSafety: '請確認安全提示後繼續。',
      forms: { capsule: '膠囊／片劑', gummy: '軟糖', powder: '粉末／沖劑', liquid: '液體', chewable: '咀嚼片' },
      ages: { '18-30': '18–30歲', '31-45': '31–45歲', '46-60': '46–60歲', '60plus': '60歲以上', child: '為兒童選購', prenatal: '懷孕／餵哺期' },
      noSelection: '未有選擇', noPreference: '冇特別要求', diets: { vegetarian: '素食膠囊（只顯示有列明產品）', 'sugar-free': '無糖', 'gluten-free': '無麩質' },
      habits: { screen: '長時間使用電子產品', sleep: '作息時間不規律', diet: '飲食種類比較少', outdoor: '較少戶外活動', stress: '工作／生活壓力較大', digestion: '經常外賣或飲食時間不定' },
      habitInsights: { screen: '長時間使用電子產品時，記得定時休息及保持合適光線。', sleep: '作息時間較不規律，日間精神及放鬆需要可能值得優先留意。', diet: '飲食種類較少時，可以先檢視日常膳食是否足夠均衡。', outdoor: '較少戶外活動時，可留意日常活動量及飲食營養來源。', stress: '工作或生活壓力較大時，規律休息及放鬆習慣同樣重要。', digestion: '經常外賣或飲食時間不定時，可先留意膳食纖維及飲食節奏。' },
      resultCopy: '以下包括你揀選嘅所有保健方向；每個方向會由較大候選庫揀出最多 2 款現貨產品，再由你揀一款較適合自己嘅產品。', defaultInsight: '你可以由最關注嘅方向開始，配合均衡飲食及規律生活習慣。',
      vegetarianMatches: '只顯示產品名稱清楚列明「素食膠囊」嘅現貨產品；共 {count} 款可比較', preferredMatches: '已優先顯示符合「{form}」偏好嘅產品；共 {count} 款可比較', closestMatches: '暫時未有完全符合「{form}」偏好嘅現貨產品，以下為最接近選擇', compareMatches: '比較 {count} 款現貨產品，請揀一款較適合你嘅產品',
      noVegetarian: '暫時未有產品名稱清楚列明「素食膠囊」而且有現貨嘅選擇，因此不會推薦未經確認嘅產品。', noProducts: '暫時未有符合條件嘅現貨產品。你可以修改偏好再查看結果。', chooseInCategory: '在{category}選擇{product}', chooseThis: '揀選呢款', matchFormat: '符合形式：{form}', averageUnit: '平均約 {price}／{unit}', unitTablet: '片', unitCapsule: '粒', chooseOptions: '選擇選項', addToCart: '加入購物車', discountedTotal: '{discount}後預計總價', totalAfterDiscount: '優惠後預計總價', discount95: '95 折', discount90: '9 折', discountPercent: '{percent}% 折扣', adding: '加入中…', added: '已加入購物車 ✓', addFailed: '未能加入，請重試', addingBundle: '加入建議組合中…',
      resultTitle: 'HealthExpress-Mart 保健需要小測驗結果', date: '日期：{value}', goals: '保健方向：{value}', age: '年齡組別：{value}', habitsLine: '日常習慣：{value}', form: '產品形式：{value}', diet: '飲食偏好：{value}', safety: '安全確認：{value}', safetyReview: '購買或服用前，請先向醫生或藥劑師確認', safetyClear: '以上特別情況都不適用', recommendations: '推薦產品', selected: '✓ 已選', bullet: '•', price: '售價：{value}', reason: '推薦原因：{value}', link: '產品連結：{value}', selectedBundle: '已選建議組合：{count} 件', originalTotal: '原價合計：{value}', discountedLine: '{discount}後合計：{value}', saved: '可節省：{value}', disclaimerHeading: '結果聲明', fallbackDisclaimer: '小測驗結果及產品配對只供一般參考，並非醫療診斷或醫療建議。', copied: '已複製測驗結果 ✓', copyFailed: '未能自動複製，請改用下載結果。', downloaded: '已下載測驗結果 ✓', filename: 'HealthExpress-Mart-保健小測驗結果'
    };
    const en = {
      ...zh, start: 'Start', maxGoals: 'You can choose up to 3 goals.', chooseGoal: 'Choose at least 1 wellness goal.', chooseAge: 'Choose an age group.', confirmSafety: 'Complete the safety check to continue.',
      forms: { capsule: 'Capsules / tablets', gummy: 'Gummies', powder: 'Powder / drink mix', liquid: 'Liquid', chewable: 'Chewable tablets' }, ages: { '18-30': '18–30', '31-45': '31–45', '46-60': '46–60', '60plus': '60 or above', child: 'Shopping for a child', prenatal: 'Pregnant / breastfeeding' }, noSelection: 'Not selected', noPreference: 'No preference', diets: { vegetarian: 'Vegetarian capsules (clearly labelled products only)', 'sugar-free': 'Sugar free', 'gluten-free': 'Gluten free' }, habits: { screen: 'Long hours using digital screens', sleep: 'Irregular sleep schedule', diet: 'Limited variety in daily diet', outdoor: 'Limited outdoor activity', stress: 'High work / life stress', digestion: 'Frequent takeaway meals or irregular mealtimes' },
      habitInsights: { screen: 'Take regular breaks and use suitable lighting during long periods of screen use.', sleep: 'An irregular sleep schedule may make daytime energy and relaxation worth prioritising.', diet: 'If your diet lacks variety, first review whether your everyday meals are balanced.', outdoor: 'With limited outdoor activity, consider your daily movement and dietary nutrient sources.', stress: 'When work or life stress is high, regular rest and relaxation habits also matter.', digestion: 'With frequent takeaway meals or irregular mealtimes, consider dietary fibre and a steadier eating schedule.' },
      resultCopy: 'All selected wellness goals are shown below. Each goal displays up to two in-stock options from a larger candidate pool, so you can choose the better fit.', defaultInsight: 'Start with your highest-priority goal alongside a balanced diet and regular lifestyle habits.', vegetarianMatches: 'Showing only in-stock products clearly labelled “vegetarian capsules”; {count} options to compare', preferredMatches: 'Prioritising products matching your “{form}” preference; {count} options to compare', closestMatches: 'No in-stock products fully match “{form}”; showing the closest alternatives', compareMatches: 'Compare {count} in-stock options and choose the better fit', noVegetarian: 'No in-stock products are clearly labelled “vegetarian capsules”, so unverified products will not be recommended.', noProducts: 'No in-stock products currently match. Edit your preferences and view the results again.', chooseInCategory: 'Choose {product} for {category}', chooseThis: 'Choose this one', matchFormat: 'Format match: {form}', averageUnit: 'About {price} / {unit}', unitTablet: 'tablet', unitCapsule: 'capsule', chooseOptions: 'Choose options', addToCart: 'Add to cart', discountedTotal: 'Estimated total after {discount}', totalAfterDiscount: 'Estimated total after discount', discount95: '5% off', discount90: '10% off', discountPercent: '{percent}% off', adding: 'Adding…', added: 'Added to cart ✓', addFailed: 'Could not add. Please try again.', addingBundle: 'Adding recommended bundle…',
      resultTitle: 'HealthExpress-Mart Supplement Quiz Results', date: 'Date: {value}', goals: 'Wellness goals: {value}', age: 'Age group: {value}', habitsLine: 'Lifestyle habits: {value}', form: 'Product format: {value}', diet: 'Dietary preference: {value}', safety: 'Safety check: {value}', safetyReview: 'Check with a doctor or pharmacist before buying or taking any product', safetyClear: 'None of the special situations apply', recommendations: 'Recommended products', selected: '✓ Selected', bullet: '•', price: 'Price: {value}', reason: 'Why it matches: {value}', link: 'Product link: {value}', selectedBundle: 'Selected bundle: {count} items', originalTotal: 'Original total: {value}', discountedLine: 'Total after {discount}: {value}', saved: 'You save: {value}', disclaimerHeading: 'Results disclaimer', fallbackDisclaimer: 'Quiz results and product matches are for general reference only and are not a medical diagnosis or medical advice.', copied: 'Quiz results copied ✓', copyFailed: 'Could not copy automatically. Please download the results instead.', downloaded: 'Quiz results downloaded ✓', filename: 'HealthExpress-Mart-supplement-quiz-results'
    };
    const zhCn = {
      ...zh,
      start: '开始', maxGoals: '最多可以选择 3 个方向。', chooseGoal: '请至少选择 1 个最关注方向。', chooseAge: '请选择适用年龄组别。', confirmSafety: '请完成安全确认后继续。',
      forms: { capsule: '胶囊／片剂', gummy: '软糖', powder: '粉末／冲剂', liquid: '液体', chewable: '咀嚼片' },
      ages: { '18-30': '18–30岁', '31-45': '31–45岁', '46-60': '46–60岁', '60plus': '60岁以上', child: '为儿童选购', prenatal: '怀孕／哺乳期' },
      noSelection: '未选择', noPreference: '无特别要求', diets: { vegetarian: '素食胶囊（只显示有明确标示的产品）', 'sugar-free': '无糖', 'gluten-free': '无麸质' },
      habits: { screen: '长时间使用电子产品', sleep: '作息时间不规律', diet: '饮食种类比较少', outdoor: '较少户外活动', stress: '工作／生活压力较大', digestion: '经常外卖或饮食时间不定' },
      habitInsights: { screen: '长时间使用电子产品时，请定时休息并保持合适光线。', sleep: '作息时间不规律时，日间精神及放松需要可能值得优先留意。', diet: '饮食种类较少时，可以先检查日常膳食是否足够均衡。', outdoor: '较少户外活动时，可留意日常活动量及饮食营养来源。', stress: '工作或生活压力较大时，规律休息及放松习惯同样重要。', digestion: '经常外卖或饮食时间不定时，可先留意膳食纤维及饮食节奏。' },
      resultCopy: '以下包括你选择的所有保健方向；每个方向会从较大候选库选出最多 2 款现货产品，再由你选择较适合自己的产品。', defaultInsight: '你可以从最关注的方向开始，配合均衡饮食及规律生活习惯。', vegetarianMatches: '只显示产品名称明确标示“素食胶囊”的现货产品；共 {count} 款可比较', preferredMatches: '已优先显示符合“{form}”偏好的产品；共 {count} 款可比较', closestMatches: '暂时没有完全符合“{form}”偏好的现货产品，以下是最接近选择', compareMatches: '比较 {count} 款现货产品，请选择较适合你的产品',
      noVegetarian: '暂时没有产品名称明确标示“素食胶囊”并且有现货的选择，因此不会推荐未经确认的产品。', noProducts: '暂时没有符合条件的现货产品。你可以修改偏好后再查看结果。', chooseInCategory: '在{category}选择{product}', chooseThis: '选择这款', matchFormat: '符合形式：{form}', averageUnit: '平均约 {price}／{unit}', unitTablet: '片', unitCapsule: '粒', chooseOptions: '选择选项', addToCart: '加入购物车', discountedTotal: '{discount}后预计总价', totalAfterDiscount: '优惠后预计总价', adding: '加入中…', added: '已加入购物车 ✓', addFailed: '未能加入，请重试', addingBundle: '正在加入推荐组合…',
      resultTitle: 'HealthExpress-Mart 保健需要测试结果', date: '日期：{value}', goals: '保健方向：{value}', age: '年龄组别：{value}', habitsLine: '日常习惯：{value}', form: '产品形式：{value}', diet: '饮食偏好：{value}', safety: '安全确认：{value}', safetyReview: '购买或服用前，请先向医生或药剂师确认', safetyClear: '以上特别情况都不适用', recommendations: '推荐产品', selected: '✓ 已选择', price: '售价：{value}', reason: '推荐原因：{value}', link: '产品链接：{value}', selectedBundle: '已选推荐组合：{count} 件', originalTotal: '原价合计：{value}', discountedLine: '{discount}后合计：{value}', saved: '可节省：{value}', disclaimerHeading: '结果声明', fallbackDisclaimer: '测试结果及产品配对只供一般参考，并非医疗诊断或医疗建议。', copied: '已复制测试结果 ✓', copyFailed: '未能自动复制，请改用下载结果。', downloaded: '已下载测试结果 ✓', filename: 'HealthExpress-Mart-保健测试结果'
    };
    if (this.locale.startsWith('en')) return en;
    if (this.locale.startsWith('zh-cn') || this.locale.startsWith('zh-hans')) return zhCn;
    return zh;
  }

  t(key, variables = {}) {
    const value = key.split('.').reduce((current, part) => current?.[part], this.messages) ?? key;
    return Object.entries(variables).reduce((text, [name, replacement]) => text.replaceAll(`{${name}}`, replacement), String(value));
  }

  readCategories() {
    this.root.querySelectorAll('[data-quiz-category]').forEach((element) => {
      const script = element.querySelector('script[type="application/json"]');
      let products = [];
      try {
        products = JSON.parse(script?.textContent || '[]');
      } catch (error) {
        console.warn('Supplement quiz product data could not be read.', error);
      }
      this.categories.set(element.dataset.key, {
        key: element.dataset.key,
        title: element.dataset.title,
        insight: element.dataset.insight,
        reason: element.dataset.reason,
        products
      });
    });
  }

  bindEvents() {
    this.root.addEventListener('click', (event) => {
      const action = event.target.closest('[data-quiz-action]');
      if (!action) return;
      const name = action.dataset.quizAction;
      if (name === 'start') {
        this.trackOnce('supplement_quiz_start', {}, 'start');
        this.showScreen('goals');
      }
      if (name === 'back-goals') this.showScreen('intro');
      if (name === 'next-details' && this.validateGoals()) this.showScreen('details');
      if (name === 'back-details') this.showScreen('goals');
      if (name === 'next-safety' && this.validateDetails()) this.showScreen('safety');
      if (name === 'back-safety') this.showScreen('details');
      if (name === 'results' && this.validateSafety()) this.renderResults();
      if (name === 'edit-goals') this.showScreen('goals');
      if (name === 'edit-details') this.showScreen('details');
      if (name === 'restart') this.restart();
      if (name === 'bundle') this.addBundle(action);
      if (name === 'add-one') this.addOne(action);
      if (name === 'copy-results') this.copyResults();
      if (name === 'download-results') this.downloadResults();
    });

    this.root.addEventListener('change', (event) => {
      if (event.target.matches('input[name="quiz_goal"]')) this.enforceGoalLimit(event.target);
      if (event.target.matches('[data-bundle-choice]')) this.updateBundleSelection();
      if (event.target.matches('input[name="quiz_safety"]')) {
        const checked = this.root.querySelector('input[name="quiz_safety"]:checked');
        this.root.querySelector('[data-safety-note]').hidden = checked?.value !== 'review';
      }
    });

  }

  track(event, details = {}) {
    const payload = { event, ...details };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    if (new URLSearchParams(window.location.search).has('analyticstest')) {
      console.info('[SupplementQuizAnalytics]', JSON.stringify(payload));
    }
  }

  sessionKey(name) {
    return `health_express_quiz_${this.analyticsVersion}_${name}`;
  }

  sessionHas(name) {
    try {
      return window.sessionStorage.getItem(this.sessionKey(name)) === '1';
    } catch (error) {
      return false;
    }
  }

  markSession(name) {
    try {
      window.sessionStorage.setItem(this.sessionKey(name), '1');
    } catch (error) {
      // Private browsing can block storage; analytics must never block the quiz.
    }
  }

  trackOnce(event, details, sessionName) {
    if (this.sessionHas(sessionName)) return false;
    this.markSession(sessionName);
    this.track(event, details);
    return true;
  }

  sendAggregate(eventName, details = {}) {
    if (!this.analyticsEndpoint) return;
    const payload = JSON.stringify({
      event: eventName,
      version: this.analyticsVersion,
      ...details
    });
    try {
      const body = new Blob([payload], { type: 'text/plain;charset=UTF-8' });
      if (navigator.sendBeacon?.(this.analyticsEndpoint, body)) return;
    } catch (error) {
      // Fall through to a keepalive request.
    }
    fetch(this.analyticsEndpoint, {
      method: 'POST',
      body: payload,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      keepalive: true,
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    }).catch(() => {});
  }

  showScreen(name) {
    this.currentScreen = name;
    this.screens.forEach((screen) => {
      screen.hidden = screen.dataset.quizScreen !== name;
    });
    const stepMap = { intro: 0, goals: 1, details: 2, safety: 3, results: 4 };
    const step = stepMap[name];
    const bar = this.root.querySelector('[data-progress-bar]');
    const label = this.root.querySelector('[data-progress-label]');
    bar.style.width = `${(step / 4) * 100}%`;
    label.textContent = step === 0 ? this.t('start') : `${step} / 4`;
    const heading = this.root.querySelector(`[data-quiz-screen="${name}"] h2`);
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (name !== 'intro') this.track('supplement_quiz_step', { quiz_step: name });
  }

  selectedValues(name) {
    return [...this.root.querySelectorAll(`input[name="${name}"]:checked`)].map((input) => input.value);
  }

  selectedValue(name) {
    return this.root.querySelector(`input[name="${name}"]:checked`)?.value || '';
  }

  showError(screen, message = '') {
    const error = this.root.querySelector(`[data-quiz-screen="${screen}"] [data-error]`);
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  enforceGoalLimit(changedInput) {
    const selected = this.selectedValues('quiz_goal');
    if (selected.length > 3) {
      changedInput.checked = false;
      this.showError('goals', this.t('maxGoals'));
      return;
    }
    this.showError('goals');
  }

  validateGoals() {
    const selected = this.selectedValues('quiz_goal');
    if (!selected.length) {
      this.showError('goals', this.t('chooseGoal'));
      return false;
    }
    this.showError('goals');
    return true;
  }

  validateDetails() {
    if (!this.selectedValue('quiz_age')) {
      this.showError('details', this.t('chooseAge'));
      return false;
    }
    this.showError('details');
    return true;
  }

  validateSafety() {
    if (!this.selectedValue('quiz_safety')) {
      this.showError('safety', this.t('confirmSafety'));
      return false;
    }
    this.showError('safety');
    return true;
  }

  answers() {
    return {
      goals: this.selectedValues('quiz_goal'),
      habits: this.selectedValues('quiz_habit'),
      age: this.selectedValue('quiz_age'),
      form: this.selectedValue('quiz_form'),
      dietary: this.selectedValue('quiz_dietary'),
      safetyReview: this.selectedValue('quiz_safety') === 'review'
    };
  }

  productForm(product) {
    if (product.form) return String(product.form).toLowerCase();
    const searchable = [product.title, ...(product.tags || [])].join(' ').normalize('NFKC').toLowerCase();
    if (/軟糖|橡皮糖|gumm/.test(searchable)) return 'gummy';
    if (/咀嚼|chew|速溶|dissolv/.test(searchable)) return 'chewable';
    if (/粉劑|營養粉|粉末|powder/.test(searchable)) return 'powder';
    if (/液體|口服液|liquid/.test(searchable)) return 'liquid';
    if (/膠囊|軟膠囊|片劑|capsule|caplet|tablet|softgel|vcap/.test(searchable)) return 'capsule';
    return '';
  }

  formLabel(value) {
    return this.messages.forms[value] || '';
  }

  ageLabel(value) {
    return this.messages.ages[value] || this.t('noSelection');
  }

  dietaryLabel(value) {
    return value === 'none' ? this.t('noPreference') : (this.messages.diets[value] || this.t('noPreference'));
  }

  habitLabel(value) {
    return this.messages.habits[value] || '';
  }

  isVerifiedVegetarian(product) {
    const title = String(product.title || '').normalize('NFKC').toLowerCase();
    return /素食膠囊|素膠囊|vegetarian\s+(?:capsules?|vcaps?)|veg(?:gie)?\s+(?:capsules?|caps?|vcaps?)|v-?caps?\b/.test(title);
  }

  unitQuantity(product) {
    const title = String(product.title || '').normalize('NFKC');
    const unitPattern = '素食膠囊|素膠囊|軟膠囊|膠囊|粒|片|capsules?|caplets?|tablets?|softgels?|gummies?|vcaps?';
    const bundleMatch = title.match(new RegExp(`(\\d{1,4})\\s*\\+\\s*(\\d{1,4})\\s*(${unitPattern})`, 'i'));
    if (bundleMatch) {
      return {
        count: Number(bundleMatch[1]) + Number(bundleMatch[2]),
        label: /片|caplet|tablet/i.test(bundleMatch[3]) ? this.t('unitTablet') : this.t('unitCapsule')
      };
    }
    const matches = [...title.matchAll(new RegExp(`(\\d{1,4})\\s*(${unitPattern})`, 'gi'))];
    const match = matches[matches.length - 1];
    if (!match) return null;
    return {
      count: Number(match[1]),
      label: /片|caplet|tablet/i.test(match[2]) ? this.t('unitTablet') : this.t('unitCapsule')
    };
  }

  rankProducts(category, answers) {
    const normalized = (value) => String(value || '').toLowerCase();
    const preferredForm = answers.form && answers.form !== 'none' ? normalized(answers.form) : '';
    const scored = category.products
      .filter((product) => product.available && product.variantAvailable)
      .filter((product) => answers.dietary !== 'vegetarian' || this.isVerifiedVegetarian(product))
      .map((product, index) => {
        const quizForm = this.productForm(product);
        let score = Number(product.priority || 0) * 10 - index;
        if ((product.goals || []).map(normalized).includes(normalized(category.key))) score += 30;
        if ((product.ageGroups || []).map(normalized).includes(normalized(answers.age))) score += 8;
        if (answers.dietary && answers.dietary !== 'none' && (product.dietary || []).map(normalized).includes(normalized(answers.dietary))) score += 6;
        return {
          ...product,
          quizForm,
          preferredFormMatch: Boolean(preferredForm && quizForm === preferredForm),
          quizScore: score
        };
      })
      .sort((a, b) => b.quizScore - a.quizScore);

    if (!scored.length) return [];
    const matching = preferredForm ? scored.filter((product) => product.preferredFormMatch) : scored;
    const remaining = preferredForm ? scored.filter((product) => !product.preferredFormMatch) : [];
    const result = [];
    const addPool = (pool) => {
      const usedVendors = new Set(result.map((product) => normalized(product.vendor)));
      pool.forEach((product) => {
        if (result.length >= 2 || usedVendors.has(normalized(product.vendor))) return;
        result.push(product);
        usedVendors.add(normalized(product.vendor));
      });
      pool.forEach((product) => {
        if (result.length >= 2 || result.some((item) => item.id === product.id)) return;
        result.push(product);
      });
    };
    addPool(matching);
    if (result.length < 2) addPool(remaining);
    return result.slice(0, 2);
  }

  habitInsights(habits) {
    return habits.map((habit) => this.messages.habitInsights[habit]).filter(Boolean);
  }

  renderResults() {
    const answers = this.answers();
    const categories = answers.goals.map((key) => this.categories.get(key)).filter(Boolean);
    const primary = categories[0];
    if (!primary) return;

    this.root.querySelector('[data-result-title]').textContent = categories.map((category) => category.title).join(this.locale.startsWith('en') ? ', ' : '、');
    this.root.querySelector('[data-result-copy]').textContent = this.t('resultCopy');

    const insights = this.habitInsights(answers.habits);
    const insightList = this.root.querySelector('[data-result-insights]');
    insightList.innerHTML = '';
    (insights.length ? insights : [this.t('defaultInsight')]).forEach((text) => {
      const item = document.createElement('li');
      item.textContent = text;
      insightList.appendChild(item);
    });

    const recommendationRoot = this.root.querySelector('[data-result-recommendations]');
    recommendationRoot.innerHTML = '';
    this.resultProducts = [];
    categories.forEach((category) => {
      const products = this.rankProducts(category, answers);
      if (!products.length) {
        recommendationRoot.appendChild(this.buildEmptyRecommendation(category, answers));
        return;
      }
      this.resultProducts.push({ category, products });
      recommendationRoot.appendChild(this.buildRecommendation(category, products, answers));
    });

    this.updateBundleSelection();
    this.root.querySelector('[data-bundle-safety]').hidden = !answers.safetyReview;

    const saveStatus = this.root.querySelector('[data-save-status]');
    if (saveStatus) saveStatus.hidden = true;

    this.showScreen('results');
    this.track('supplement_quiz_result_view', { quiz_goal_count: answers.goals.length });
    if (this.trackOnce('supplement_quiz_complete', { quiz_goal_count: answers.goals.length }, 'complete')) {
      this.sendAggregate('complete', {
        goals: answers.goals,
        age: answers.age,
        habits: answers.habits,
        form: answers.form && answers.form !== 'none' ? answers.form : 'no_preference',
        dietary: answers.dietary && answers.dietary !== 'none' ? answers.dietary : 'no_preference'
      });
    }
  }

  buildRecommendation(category, products, answers) {
    const section = document.createElement('section');
    section.className = 'supplement-quiz__recommendation';

    const heading = document.createElement('div');
    heading.className = 'supplement-quiz__recommendation-heading';
    const title = document.createElement('h3');
    title.textContent = category.title;
    const copy = document.createElement('p');
    const matchingCount = products.filter((product) => product.preferredFormMatch).length;
    const preferredFormLabel = this.formLabel(answers.form);
    if (answers.dietary === 'vegetarian') {
      copy.textContent = this.t('vegetarianMatches', { count: products.length });
    } else if (preferredFormLabel && matchingCount) {
      copy.textContent = this.t('preferredMatches', { form: preferredFormLabel, count: products.length });
    } else if (preferredFormLabel) {
      copy.textContent = this.t('closestMatches', { form: preferredFormLabel });
    } else {
      copy.textContent = this.t('compareMatches', { count: products.length });
    }
    heading.append(title, copy);

    const grid = document.createElement('div');
    grid.className = 'supplement-quiz__products';
    products.forEach((product) => grid.appendChild(this.buildProductCard(product, category)));
    section.append(heading, grid);
    return section;
  }

  buildEmptyRecommendation(category, answers) {
    const section = document.createElement('section');
    section.className = 'supplement-quiz__recommendation';
    const heading = document.createElement('div');
    heading.className = 'supplement-quiz__recommendation-heading';
    const title = document.createElement('h3');
    title.textContent = category.title;
    heading.appendChild(title);
    const note = document.createElement('p');
    note.className = 'supplement-quiz__no-match';
    note.textContent = answers.dietary === 'vegetarian' ? this.t('noVegetarian') : this.t('noProducts');
    section.append(heading, note);
    return section;
  }

  buildProductCard(product, category) {
    const card = document.createElement('article');
    card.className = 'supplement-quiz__product';
    card.dataset.variantId = product.variantId;

    const selector = document.createElement('label');
    selector.className = 'supplement-quiz__product-selector';
    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = `quiz_bundle_${category.key}`;
    radio.value = product.variantId;
    radio.dataset.bundleChoice = category.key;
    radio.setAttribute('aria-label', this.t('chooseInCategory', { category: category.title, product: product.title }));
    const selectorText = document.createElement('span');
    selectorText.textContent = this.t('chooseThis');
    selector.append(radio, selectorText);

    const imageWrap = document.createElement('a');
    imageWrap.className = 'supplement-quiz__product-image-wrap';
    imageWrap.href = product.url;
    if (product.image) {
      const image = document.createElement('img');
      image.className = 'supplement-quiz__product-image';
      image.src = product.image;
      image.alt = product.imageAlt || product.title;
      image.loading = 'lazy';
      image.width = 360;
      image.height = 360;
      imageWrap.appendChild(image);
    }

    const content = document.createElement('div');
    content.className = 'supplement-quiz__product-content';
    const vendor = document.createElement('div');
    vendor.className = 'supplement-quiz__product-vendor';
    vendor.textContent = product.vendor;
    const title = document.createElement('a');
    title.className = 'supplement-quiz__product-title';
    title.href = product.url;
    title.textContent = product.title;
    const reason = document.createElement('p');
    reason.className = 'supplement-quiz__product-reason';
    reason.textContent = category.reason;

    const badges = document.createElement('div');
    badges.className = 'supplement-quiz__badges';
    if (product.origin) {
      const badge = document.createElement('span');
      badge.className = 'supplement-quiz__badge';
      badge.textContent = product.origin;
      badges.appendChild(badge);
    }
    if (product.preferredFormMatch) {
      const preferenceBadge = document.createElement('span');
      preferenceBadge.className = 'supplement-quiz__badge supplement-quiz__badge--match';
      preferenceBadge.textContent = this.t('matchFormat', { form: this.formLabel(product.quizForm) });
      badges.appendChild(preferenceBadge);
    }

    const price = document.createElement('div');
    price.className = 'supplement-quiz__product-price';
    const current = document.createElement('span');
    current.textContent = product.price;
    price.appendChild(current);
    if (product.compareAt) {
      const compare = document.createElement('span');
      compare.className = 'supplement-quiz__product-compare';
      compare.textContent = product.compareAt;
      price.appendChild(compare);
    }

    const quantity = this.unitQuantity(product);
    let unitPrice = null;
    if (quantity?.count > 0 && Number(product.priceCents) > 0) {
      unitPrice = document.createElement('div');
      unitPrice.className = 'supplement-quiz__unit-price';
      unitPrice.textContent = this.t('averageUnit', { price: this.formatMoney(Number(product.priceCents) / quantity.count), unit: quantity.label });
    }

    let action;
    if (Number(product.variantCount) > 1) {
      action = document.createElement('a');
      action.href = product.url;
      action.textContent = this.t('chooseOptions');
    } else {
      action = document.createElement('button');
      action.type = 'button';
      action.dataset.quizAction = 'add-one';
      action.dataset.variantId = product.variantId;
      action.textContent = this.t('addToCart');
    }
    action.className = 'button button--secondary supplement-quiz__product-action';
    content.append(vendor, title, reason, badges, price);
    if (unitPrice) content.appendChild(unitPrice);
    content.appendChild(action);
    card.append(selector, imageWrap, content);
    return card;
  }

  updateBundleSelection() {
    const bundleButton = this.root.querySelector('[data-quiz-action="bundle"]');
    if (!bundleButton) return;
    const answers = this.answers();
    const selected = [...this.root.querySelectorAll('[data-bundle-choice]:checked')];
    const expected = this.resultProducts.length;
    const variantIds = selected.map((input) => input.value).filter(Boolean);
    const selectedProducts = selected.map((input) => {
      const entry = this.resultProducts.find((result) => result.category.key === input.dataset.bundleChoice);
      return entry?.products.find((product) => String(product.variantId) === String(input.value));
    }).filter(Boolean);
    const subtotal = selectedProducts.reduce((total, product) => total + Number(product.priceCents || 0), 0);
    const discountRate = this.discountRateForCount(variantIds.length);
    const discountedTotal = variantIds.length
      ? Math.round(subtotal * (100 - discountRate) / 100)
      : subtotal;
    const savings = subtotal - discountedTotal;
    const qualifies = expected >= 1 && variantIds.length === expected;
    bundleButton.dataset.items = JSON.stringify(variantIds);
    bundleButton.querySelector('[data-bundle-count]').textContent = String(variantIds.length);
    bundleButton.disabled = answers.safetyReview || !qualifies;
    this.root.querySelector('[data-bundle-subtotal]').textContent = this.formatMoney(subtotal);
    this.root.querySelector('[data-bundle-total]').textContent = this.formatMoney(discountedTotal);
    this.root.querySelector('[data-bundle-savings]').textContent = this.formatMoney(savings);
    const totalLabel = this.root.querySelector('[data-bundle-total-label]');
    if (totalLabel) {
      totalLabel.textContent = variantIds.length
        ? this.t('discountedTotal', { discount: this.discountLabel(discountRate) })
        : this.t('totalAfterDiscount');
    }
    const selectionError = this.root.querySelector('[data-bundle-selection]');
    if (selectionError) selectionError.hidden = answers.safetyReview || expected < 1 || variantIds.length === expected;
  }

  discountRateForCount(count) {
    const rate = count >= 2
      ? Number(this.root.dataset.discountPercentMulti || 0)
      : Number(this.root.dataset.discountPercentSingle || 0);
    return Math.max(0, Math.min(100, rate));
  }

  discountCodeForCount(count) {
    return count >= 2
      ? this.root.dataset.discountCodeMulti
      : this.root.dataset.discountCodeSingle;
  }

  discountLabel(rate) {
    const discount = Math.max(0, Math.min(100, Number(rate || 0)));
    if (discount === 5) return this.t('discount95');
    if (discount === 10) return this.t('discount90');
    return this.t('discountPercent', { percent: discount });
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'HKD';
    try {
      return new Intl.NumberFormat(this.locale || 'zh-HK', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  async addOne(button) {
    const original = button.textContent;
    button.disabled = true;
    button.textContent = this.t('adding');
    try {
      await this.addItems([button.dataset.variantId]);
      button.textContent = this.t('added');
      this.track('supplement_quiz_add_to_cart', { quiz_variant_id: button.dataset.variantId });
    } catch (error) {
      button.textContent = this.t('addFailed');
      button.disabled = false;
      console.warn(error);
      return;
    }
    window.setTimeout(() => {
      button.textContent = original;
      button.disabled = false;
    }, 2200);
  }

  async addBundle(button) {
    let items = [];
    try {
      items = JSON.parse(button.dataset.items || '[]');
    } catch (error) {
      return;
    }
    if (!items.length || button.disabled) return;
    const original = button.textContent;
    button.disabled = true;
    button.textContent = this.t('addingBundle');
    try {
      await this.addItems(items);
      const discountRate = this.discountRateForCount(items.length);
      this.track('supplement_quiz_bundle_add', {
        quiz_bundle_size: items.length,
        quiz_discount_percent: discountRate
      });
      this.sendAggregate('bundle_add', {
        bundle_size: items.length,
        discount_percent: discountRate,
        product_variant_ids: items.map(String),
        product_titles: items.map((variantId) => {
          for (const entry of this.resultProducts) {
            const product = entry.products.find((item) => String(item.variantId) === String(variantId));
            if (product) return product.title;
          }
          return '';
        }).filter(Boolean)
      });
      const discountCode = this.discountCodeForCount(items.length);
      const rootUrl = window.Shopify?.routes?.root || '/';
      window.location.href = discountCode
        ? `${rootUrl}discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(`${rootUrl}cart`)}`
        : `${rootUrl}cart`;
    } catch (error) {
      button.textContent = this.t('addFailed');
      button.disabled = false;
      console.warn(error);
      window.setTimeout(() => { button.textContent = original; }, 2200);
    }
  }

  async addItems(variantIds) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })) })
    });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }

  buildResultText() {
    const answers = this.answers();
    const categoryNames = answers.goals
      .map((key) => this.categories.get(key)?.title)
      .filter(Boolean);
    const habitNames = answers.habits.map((habit) => this.habitLabel(habit)).filter(Boolean);
    const separator = this.locale.startsWith('en') ? ', ' : '、';
    const lines = [
      this.t('resultTitle'),
      this.t('date', { value: new Intl.DateTimeFormat(this.locale || 'zh-HK', { dateStyle: 'long' }).format(new Date()) }),
      '',
      this.t('goals', { value: categoryNames.join(separator) || this.t('noSelection') }),
      this.t('age', { value: this.ageLabel(answers.age) }),
      this.t('habitsLine', { value: habitNames.join(separator) || this.t('noSelection') }),
      this.t('form', { value: this.formLabel(answers.form) || this.t('noPreference') }),
      this.t('diet', { value: this.dietaryLabel(answers.dietary) }),
      this.t('safety', { value: answers.safetyReview ? this.t('safetyReview') : this.t('safetyClear') }),
      '',
      this.t('recommendations')
    ];

    this.resultProducts.forEach(({ category, products }) => {
      const selectedVariant = this.root.querySelector(`input[name="quiz_bundle_${category.key}"]:checked`)?.value;
      lines.push('', `【${category.title}】`);
      products.forEach((product) => {
        const chosen = String(product.variantId) === String(selectedVariant);
        const quantity = this.unitQuantity(product);
        const unitPrice = quantity?.count > 0 && Number(product.priceCents) > 0
          ? ` | ${this.t('averageUnit', { price: this.formatMoney(Number(product.priceCents) / quantity.count), unit: quantity.label })}`
          : '';
        lines.push(`${chosen ? this.t('selected') : this.t('bullet')} ${product.vendor} ${product.title}`.trim());
        lines.push(`  ${this.t('price', { value: `${product.price}${unitPrice}` })}`);
        lines.push(`  ${this.t('reason', { value: category.reason })}`);
        lines.push(`  ${this.t('link', { value: new URL(product.url, window.location.origin).href })}`);
      });
    });

    if (!this.resultProducts.length) lines.push(this.t('noProducts'));

    const selectedProducts = [...this.root.querySelectorAll('[data-bundle-choice]:checked')].map((input) => {
      const entry = this.resultProducts.find((result) => result.category.key === input.dataset.bundleChoice);
      return entry?.products.find((product) => String(product.variantId) === String(input.value));
    }).filter(Boolean);
    if (selectedProducts.length) {
      const subtotal = selectedProducts.reduce((total, product) => total + Number(product.priceCents || 0), 0);
      const discountRate = this.discountRateForCount(selectedProducts.length);
      const total = Math.round(subtotal * (100 - discountRate) / 100);
      lines.push('', this.t('selectedBundle', { count: selectedProducts.length }));
      lines.push(this.t('originalTotal', { value: this.formatMoney(subtotal) }));
      lines.push(this.t('discountedLine', { discount: this.discountLabel(discountRate), value: this.formatMoney(total) }));
      lines.push(this.t('saved', { value: this.formatMoney(subtotal - total) }));
    }

    const disclaimer = this.root.querySelector('.supplement-quiz__result-disclaimer p')?.textContent.trim();
    lines.push('', this.t('disclaimerHeading'));
    lines.push(disclaimer || this.t('fallbackDisclaimer'));
    lines.push('', `HealthExpress-Mart：${window.location.origin}`);
    return lines.join('\n');
  }

  setSaveStatus(message, isError = false) {
    const status = this.root.querySelector('[data-save-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = false;
    status.classList.toggle('supplement-quiz__save-status--error', isError);
  }

  async copyResults() {
    const text = this.buildResultText();
    try {
      let copied = false;
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          copied = true;
        } catch (error) {
          copied = false;
        }
      }
      if (!copied) {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        copied = document.execCommand('copy');
        textarea.remove();
      }
      if (!copied) throw new Error('Copy command failed');
      this.setSaveStatus(this.t('copied'));
      this.track('supplement_quiz_result_copied');
    } catch (error) {
      this.setSaveStatus(this.t('copyFailed'), true);
    }
  }

  downloadResults() {
    const blob = new Blob([`\uFEFF${this.buildResultText()}`], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.t('filename')}-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
    this.setSaveStatus(this.t('downloaded'));
    this.track('supplement_quiz_result_downloaded');
  }

  restart() {
    this.root.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      input.checked = false;
    });
    this.root.querySelectorAll('[data-error]').forEach((error) => {
      error.hidden = true;
      error.textContent = '';
    });
    this.showScreen('intro');
    this.track('supplement_quiz_restart');
  }
}

const initSupplementQuizzes = (scope = document) => {
  scope.querySelectorAll('[data-supplement-quiz]').forEach((root) => {
    if (root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';
    new SupplementQuiz(root);
  });
};

initSupplementQuizzes();
document.addEventListener('shopify:section:load', (event) => initSupplementQuizzes(event.target));
